import { useEffect, useRef, useState } from 'react';

import type { DependencyList } from 'react';

const DEFAULT_DURATION_MS = 620;

/**
 * Progress from 0 to 1 on an ease-out-cubic curve, restarting whenever `deps`
 * change. Drives the market-FAQ count-up/fill-in animations (price tiles,
 * type/neighbourhood bars, sell/list gauge) so they share one timing curve.
 */
export function useEaseOutProgress(deps: DependencyList, durationMs = DEFAULT_DURATION_MS): number {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // One-time sync to the external prefers-reduced-motion signal, then bail out of the
      // rAF loop below entirely — not a cascading update.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setProgress(1);
      return;
    }

    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - elapsed, 3);
      setProgress(eased);
      if (elapsed < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return progress;
}
